/**
 * Create/edit dialog for a single AI model registry row.
 *
 * `model_id` is the backend's URL identifier for update/delete (see
 * `admin_router.py`'s `{model_id}` path param), so it is locked once a row
 * exists — the same treatment `ProviderFormDialog` gives its `name` field.
 */

import { useState, useEffect, type ReactElement, type FormEvent } from 'react';
import type { AiModel, AiModelCreate } from '@services/aiModelsService';
import {
  EMPTY_AI_MODEL_FORM,
  aiModelToFormData,
  buildAiModelBody,
  type AiModelFormData,
  type TriState,
} from './aiModelFormTypes';

interface AiModelFormDialogProps {
  open: boolean;
  model: AiModel | null;
  onClose: () => void;
  onSubmit: (body: Partial<AiModelCreate>) => void;
  isSubmitting: boolean;
  submitError: string | null;
}

export function AiModelFormDialog({
  open,
  model,
  onClose,
  onSubmit,
  isSubmitting,
  submitError,
}: AiModelFormDialogProps): ReactElement | null {
  const [form, setForm] = useState<AiModelFormData>(EMPTY_AI_MODEL_FORM);
  const isEdit = model !== null;

  useEffect(() => {
    setForm(model ? aiModelToFormData(model) : EMPTY_AI_MODEL_FORM);
  }, [model, open]);

  if (!open) return null;

  const setField = <K extends keyof AiModelFormData>(key: K, value: AiModelFormData[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit(buildAiModelBody(form));
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={isEdit ? 'Edit AI model' : 'Register AI model'}
      >
        <h2>{isEdit ? 'Edit AI Model' : 'Register AI Model'}</h2>
        <form onSubmit={handleSubmit} className="dialog__form">
          <label className="dialog__field">
            Model ID
            <input
              value={form.model_id}
              onChange={(e) => { setField('model_id', e.target.value); }}
              required
              {...(isEdit ? { disabled: true } : {})}
            />
          </label>
          <label className="dialog__field">
            Upstream
            <select
              value={form.upstream}
              onChange={(e) => { setField('upstream', e.target.value as AiModelFormData['upstream']); }}
            >
              <option value="vertex">vertex</option>
              <option value="local">local</option>
              <option value="bedrock">bedrock</option>
            </select>
          </label>
          <label className="dialog__field">
            API
            <select
              value={form.api}
              onChange={(e) => { setField('api', e.target.value as AiModelFormData['api']); }}
            >
              <option value="anthropic">anthropic</option>
              <option value="gemini">gemini</option>
            </select>
            <small className="help-text">gemini requires the vertex upstream.</small>
          </label>
          <label className="dialog__field">
            Upstream model ID
            <input
              value={form.upstream_model_id}
              onChange={(e) => { setField('upstream_model_id', e.target.value); }}
              required
            />
          </label>
          <label className="dialog__field">
            Provider
            <input
              value={form.provider}
              onChange={(e) => { setField('provider', e.target.value); }}
              required
            />
          </label>
          <label className="dialog__field">
            Region
            <input
              value={form.region}
              onChange={(e) => { setField('region', e.target.value); }}
            />
          </label>
          <label className="dialog__field dialog__field--checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => { setField('enabled', e.target.checked); }}
            />
            Enabled
          </label>

          <label className="dialog__field">
            Context window (tokens)
            <input
              type="number"
              min={1}
              value={form.context_window}
              onChange={(e) => { setField('context_window', e.target.value); }}
            />
            <small className="help-text">
              Leave empty if unknown. Every model Claude Code already knows should stay
              empty here — this field is for capabilities Claude Code cannot know on its
              own, such as a locally-served model.
            </small>
          </label>
          <label className="dialog__field">
            Max output tokens
            <input
              type="number"
              min={1}
              value={form.max_output_tokens}
              onChange={(e) => { setField('max_output_tokens', e.target.value); }}
            />
            <small className="help-text">Leave empty if unknown, for the same reason.</small>
          </label>
          <label className="dialog__field">
            Prompt caching
            <select
              value={form.supports_prompt_caching}
              onChange={(e) => {
                setField('supports_prompt_caching', e.target.value as TriState);
              }}
            >
              <option value="unknown">Unknown</option>
              <option value="true">Supported</option>
              <option value="false">Not supported</option>
            </select>
            <small className="help-text">
              Leave on Unknown unless Claude Code cannot determine caching support itself.
            </small>
          </label>
          <label className="dialog__field">
            Extended thinking
            <select
              value={form.supports_extended_thinking}
              onChange={(e) => {
                setField('supports_extended_thinking', e.target.value as TriState);
              }}
            >
              <option value="unknown">Unknown</option>
              <option value="true">Supported</option>
              <option value="false">Not supported</option>
            </select>
            <small className="help-text">Leave on Unknown for the same reason.</small>
          </label>

          {submitError && (
            <div className="dialog__error" role="alert">
              {submitError}
            </div>
          )}

          <div className="dialog__actions">
            <button type="button" onClick={onClose} className="dialog__btn dialog__btn--secondary">
              Cancel
            </button>
            <button type="submit" className="dialog__btn dialog__btn--primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
